import React, { Fragment, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  Stack,
  SvgIcon,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import produce from 'immer';

import { ReactComponent as DashboardIcon } from '@assets/sidebar/dashboard-icon.svg';
import { ReactComponent as LocationIcon } from '@assets/sidebar/location-icon.svg';
import { ReactComponent as UsersIcon } from '@assets/sidebar/users-icon.svg';
import { ReactComponent as CalendarIcon } from '@assets/sidebar/calendar-icon.svg';
import { ReactComponent as OfficeIcon } from '@assets/sidebar/office-icon.svg';
import { ReactComponent as ConfigIcon } from '@assets/sidebar/config-icon.svg';
import { ReactComponent as LogoutIcon } from '@assets/sidebar/logout-icon.svg';
import { ReactComponent as EmployeeIcon } from '@assets/sidebar/employee-icon.svg';
import { ReactComponent as VerifiedIcon } from '@assets/sidebar/verified-icon.svg';
import { ReactComponent as AppSettings } from '@assets/sidebar/app-settings.svg';
import { ReactComponent as RequestsIcon } from '@assets/sidebar/requests-icon.svg';

import logomb from '@assets/logo-mb-sistema.png';
import logoGoing2 from '@assets/logo-going2.png';

import { LayerSubmenuItem, MenuOptionItem } from './MenuItem';
import useModal from '@app/shared/hooks/use-modal';
import Popup from '../popup';
import ConfirmOrCancelButton from '../popup/confirm-cancel-button';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { logout as logoutReducer } from '@features/auth/slices/auth-slice';
import { logoutService } from '@app/features/auth/services/logout.service';
import BackdropLoading from '../backdrop-loading';
import useLoadingState from '@app/shared/hooks/use-loading-state';
import { StackScroll } from '../common-scrollbar';
import {
  PERMISSIONS,
  UserPermissionsIds,
  UserRoles,
} from '@features/access-control/dto/permissions';
import { useSourceScreen } from '@features/profile/hooks/use-source-screen';
import { LogoImage } from './styles';
import Button from '../button';

const drawerWidth = 260;
interface ItemWithRole {
  role?: UserPermissionsIds[] & UserRoles[];
}

type SidebarProps = {
  children?: React.ReactNode;
};

export type MenuOption = {
  title: string;
  icon: JSX.Element;
  type: 'group' | 'item';
  link?: string;
  submenus?: FirstLayerSubmenu[];
  role?: string[];
  color?: string;
};

export type FirstLayerSubmenu = {
  title: string;
  role?: string[];
  type: 'group' | 'item';
  link?: string;
  submenus?: SecondLayerSubMenu[];
};

export type SecondLayerSubMenu = {
  title: string;
  role?: string[];
  type: 'group' | 'item';
  link: string;
};

const useOptions = () => {
  const { permissions } = useAppSelector(state => state.permissions);
  const { userInfo } = useAppSelector(state => state.auth);

  const menuOptions: MenuOption[] = [
    {
      title: 'Dashboard',
      icon: (
        <SvgIcon color="primary">
          <DashboardIcon />
        </SvgIcon>
      ),
      type: 'item',
      link: '/dashboard',
    },
    {
      title: 'Secretaria',
      icon: (
        <SvgIcon color="primary">
          <OfficeIcon />
        </SvgIcon>
      ),
      type: 'group',
      submenus: [
        {
          title: 'Igrejas',
          role: [PERMISSIONS.CHURCH_MODULE],
          type: 'group',
          submenus: [
            {
              title: 'Setores',
              type: 'item',
              link: '/setores',
              role: [PERMISSIONS.SECTOR_READ],
            },
            {
              title: 'Congregações',
              type: 'item',
              link: '/congregacoes',
              role: [PERMISSIONS.CONGREGATION_READ],
            },
          ],
        },
        {
          title: 'Usuários',
          type: 'group',
          role: [PERMISSIONS.USER_MODULE],
          submenus: [
            {
              title: 'Visitantes',
              type: 'item',
              link: '/visitantes',
              role: [PERMISSIONS.VISITOR_READ],
            },
            {
              title: 'Membros',
              type: 'item',
              link: '/membros',
              role: [PERMISSIONS.MEMBER_READ],
            },
            {
              title: 'Grupo de Membros',
              type: 'item',
              link: '/grupo-membros',
              role: [PERMISSIONS.MEMBER_GROUP_READ],
            },
            {
              title: 'Notificar Grupos',
              type: 'item',
              link: '/notificacoes-grupo-membros',
              role: [PERMISSIONS.GROUP_NOTIFICATION_READ],
            },
            {
              title: 'Solicitações de Grupo',
              type: 'item',
              link: '/solicitacoes-grupo',
              role: [PERMISSIONS.REQUEST_GROUP_READ],
            },
            {
              title: 'Solicitação de Filiações',
              type: 'item',
              link: '/filiacoes',
              role: [PERMISSIONS.REQUEST_AFFILIATION_READ],
            },
          ],
        },
        {
          title: 'Controle de Acesso',
          type: 'group',
          role: [UserRoles.ADMIN],
          submenus: [
            {
              title: 'Perfis',
              type: 'item',
              link: '/perfis',
              role: [UserRoles.ADMIN],
            },
            {
              title: 'Atribuir Acesso',
              type: 'item',
              link: '/atribuir-acesso',
              role: [UserRoles.ADMIN],
            },
            // {
            //   title: 'Solicitar Acesso',
            //   type: 'item',
            //   link: '/solicitar-acesso',
            // },
            // {
            //   title: 'Solicitações de Acesso',
            //   type: 'item',
            //   link: '/solicitacoes-acesso',
            // },
          ],
        },
        {
          title: 'Gestão de Cargos',
          type: 'group',
          role: [PERMISSIONS.JOB_MODULE],
          submenus: [
            {
              title: 'Títulos',
              type: 'item',
              link: '/titulos',
              role: [PERMISSIONS.TITLE_READ],
            },
            {
              title: 'Funções',
              type: 'item',
              link: '/funcoes',
              role: [PERMISSIONS.FUNCTION_READ],
            },
            {
              title: 'Grupos',
              type: 'item',
              link: '/grupos',
              role: [PERMISSIONS.GROUP_READ],
            },
            {
              title: 'Alterações',
              type: 'item',
              link: '/alteracoes',
              role: [PERMISSIONS.TITLE_CHANGE_READ],
            },
            {
              title: 'Alterações Pendentes',
              type: 'item',
              link: '/solicitacoes-titulo',
              role: [PERMISSIONS.TITLE_CHANGE_PENDING_READ],
            },
          ],
        },
        {
          title: 'Eventos',
          type: 'group',
          role: [PERMISSIONS.EVENT_MODULE],
          submenus: [
            {
              title: 'Detalhes dos Eventos',
              type: 'item',
              link: '/eventos-abertos',
              role: [PERMISSIONS.EVENT_DETAILS_READ],
            },
            {
              title: 'Eventos Concluídos',
              type: 'item',
              link: '/eventos-concluidos',
              role: [PERMISSIONS.COMPLETED_EVENTS_READ],
            },
          ],
        },
        {
          title: 'Serviços',
          type: 'group',
          role: [PERMISSIONS.SECRETARY_MODULE],
          submenus: [
            {
              title: 'Transferências',
              type: 'item',
              link: '/transferencias',
              role: [PERMISSIONS.TRANSFERENCE_READ],
            },
            {
              title: 'Homologações',
              type: 'item',
              link: '/homologacoes',
              role: [PERMISSIONS.HOMOLOGATION_READ],
            },
            {
              title: 'Recomendação',
              type: 'item',
              link: '/solicitar-recomendacao',
              role: [PERMISSIONS.RECOMMENDATION_READ],
            },
            {
              title: 'Assinar Recomendação',
              type: 'item',
              link: '/recomendacoes',
              role: [PERMISSIONS.TO_SIGN_RECOMMENDATION_READ],
            },
            {
              title: 'Alteração de Dados',
              type: 'item',
              link: '/alteracoes-pendentes',
              role: [PERMISSIONS.DATA_CHANGE_READ],
            },
            {
              title: 'Contas Recuperadas',
              type: 'item',
              link: '/contas-recuperadas',
              role: [PERMISSIONS.RECOVERED_ACCOUNTS_READ],
            },
            {
              title: 'Contas a Verificar',
              type: 'item',
              link: '/contas-a-verificar',
              role: [PERMISSIONS.ACCOUNTS_TO_CHECK_READ],
            },
          ],
        },
        {
          title: 'Solicitações',
          type: 'group',
          role: [PERMISSIONS.REQUESTS_MODULE],
          submenus: [
            {
              title: 'Carteirinha Física',
              type: 'item',
              link: '/wallet-requests',
              role: [PERMISSIONS.PHYSICAL_CARD_READ],
            },
            {
              title: 'Certificados',
              type: 'item',
              link: '/certificates-requests',
              role: [PERMISSIONS.CERTIFICATES_READ],
            },
            {
              title: 'Motivos',
              type: 'item',
              link: '/motivos',
              role: [PERMISSIONS.REASONS_READ],
            },
          ],
        },
      ],
    },
    {
      title: 'APP (ADBelémSP)',
      icon: (
        <SvgIcon color="primary">
          <AppSettings />
        </SvgIcon>
      ),
      type: 'group',
      role: [PERMISSIONS.APP_MODULE],
      submenus: [
        {
          title: 'Transmissões',
          type: 'item',
          link: '/transmissoes',
          role: [PERMISSIONS.TRANSMISSIONS_READ],
        },
        {
          title: 'Notícias',
          type: 'item',
          link: '/noticias',
          role: [PERMISSIONS.NEWS_READ],
        },
        {
          title: 'Contato',
          type: 'item',
          link: '/contato',
          role: [PERMISSIONS.CONTACT_READ],
        },
        {
          title: 'Sobre',
          type: 'item',
          link: '/sobre',
          role: [PERMISSIONS.ABOUT_READ],
        },
        {
          title: 'Notificações Internas',
          type: 'item',
          link: '/notificacoes/internas',
          role: [PERMISSIONS.INTERNAL_NOTIFICATIONS_READ],
        },
        {
          title: 'Notificações Push',
          type: 'item',
          link: '/notificacoes/push',
          role: [PERMISSIONS.PUSH_NOTIFICATIONS_READ],
        },
      ],
    },
    {
      title: 'Configurações',
      icon: (
        <SvgIcon color="primary">
          <ConfigIcon />
        </SvgIcon>
      ),
      type: 'group',
      role: [PERMISSIONS.SETTING_MODULE],
      submenus: [
        {
          title: 'Usuários Admin',
          type: 'item',
          link: '/admins',
          role: [PERMISSIONS.ADMIN_USERS_READ],
        },
        {
          title: 'Exclusão de Contas',
          type: 'item',
          link: '/exclusao-de-contas',
          role: [UserRoles.SUPERADMIN],
        },
        {
          title: 'Termos de Uso',
          type: 'item',
          link: '/termos',
          role: [PERMISSIONS.TERMS_OF_USE_READ],
        },
      ],
    },
    {
      title: 'Sair do Sistema',
      icon: (
        <SvgIcon color="primary">
          <LogoutIcon />
        </SvgIcon>
      ),
      type: 'item',
      color: 'Interaction/Feedback/Erro',
      link: '/logout-system',
    },
  ];

  function filterOptions(item: unknown) {
    const menuItem = item as ItemWithRole;

    const userRole = userInfo?.erpRole;

    const isAdmin = [UserRoles.ADMIN, UserRoles.SUPERADMIN].includes(
      userRole as any,
    );
    const isSuperAdmin = userRole === UserRoles.SUPERADMIN;

    const roles = menuItem.role;

    if (!roles) return true;

    if (roles.includes(UserRoles.SUPERADMIN) && isSuperAdmin) {
      return true;
    }

    if (!roles.includes(UserRoles.SUPERADMIN) && isAdmin) {
      return true;
    }

    return menuItem.role?.some(role => permissions.includes(role));
  }

  function filterMenusAndSubMenus() {
    const menus = menuOptions.filter(filterOptions);

    const menusWithSubMenus = menus.map(item => {
      return produce(item, draft => {
        draft.submenus = draft.submenus?.filter(filterOptions);
      });
    });

    return menusWithSubMenus;
  }

  return filterMenusAndSubMenus();
};

export default function Sidebar({ children }: SidebarProps) {
  const modalConfirmLogout = useModal();
  const modalErrorLogout = useModal();
  const loading = useLoadingState();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { sourceScreen } = useSourceScreen();
  const [openedMenu, setOpenedMenu] = useState<string>('');
  const [layer, setLayer] = useState<number>(0);
  const theme = useTheme();

  useEffect(() => {
    const title = menuOptions.find(menu => {
      return (
        menu.link === pathname ||
        menu.submenus?.some(submenu => submenu.link === pathname)
      );
    })?.title;

    if (title) setOpenedMenu(title);
  }, []);

  const menuOptions = useOptions();

  const logout = useMutation(logoutService, {
    onMutate: () => {
      modalConfirmLogout.close();
      loading.open();
    },
    onError: () => {
      modalErrorLogout.open();
    },
    onSuccess: () => {
      dispatch(logoutReducer());
      navigate('/login');
    },
    onSettled: () => loading.close(),
  });

  function navigateTo(route: string) {
    navigate(route);
  }

  const activeFirstLayerItem = menuOptions.find(
    item => item.title === openedMenu,
  );

  const activeSecondLayerItem = menuOptions
    .flatMap(item => item.submenus ?? [])
    .find(subItem => subItem.title === openedMenu);

  function handlePressInMenuOption(menuOption: MenuOption) {
    if (menuOption.type === 'group') {
      setOpenedMenu(menuOption.title);
      setLayer(1);
      return;
    }
    if (!menuOption.link) return;

    if (menuOption.link === '/logout-system') modalConfirmLogout.open();
    else navigateTo(menuOption.link);
  }

  function handlePressInLayerSubmenu(menuOption: FirstLayerSubmenu) {
    if (menuOption.type === 'group') {
      setOpenedMenu(menuOption.title);
      setLayer(2);
      return;
    }

    if (!menuOption.link) return;

    navigateTo(menuOption.link);
  }

  function isRouteIsActive(link: string) {
    const mapLinks = {
      MEMBERS: '/membros',
      ACCOUNT_WITHOUT_PASSWORD: '/contas-sem-senha',
    };

    if (sourceScreen !== 'PROFILE' && mapLinks[sourceScreen] === link) {
      return true;
    }

    return pathname.includes(link);
  }

  function getMenuColor(link: string): string {
    return isRouteIsActive(link) ? 'Brand/Solid/Primary' : 'Ambient/Base/Black';
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <BackdropLoading isOpen={loading.isLoading} />

      <Popup
        text="Tem certeza que deseja sair do sistema?"
        open={modalConfirmLogout.isShowing}
        onClose={modalConfirmLogout.close}
        id="modal-sair-sistema"
      >
        <ConfirmOrCancelButton
          onPressConfirm={logout.mutate}
          onPressCancel={modalConfirmLogout.close}
        />
      </Popup>

      <Popup
        text="Falha ao fazer logout, tente novamente"
        variant="ERROR"
        open={modalErrorLogout.isShowing}
        onClose={modalErrorLogout.close}
        onPressOk={modalErrorLogout.close}
      />

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        PaperProps={{
          sx: {
            border: 0,
            boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1);',
            backgroundColor: 'Ambient/Deep/White',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <StackScroll>
          <Box
            py={4}
            px={7.75}
            display="flex"
            alignItems="center"
            justifyItems="center"
          >
            <img src={logomb} style={{ width: '100%' }} />
          </Box>
          <Divider sx={{ backgroundColor: 'Ambient/Neutrals/Line-Color' }} />

          <Stack flex={1} mt={3}>
            {layer === 0 &&
              menuOptions.map((menuOption: MenuOption, i: number) => (
                <MenuOptionItem
                  key={i}
                  menuOption={menuOption}
                  handleClick={() => {
                    handlePressInMenuOption(menuOption);
                  }}
                  openedMenu={openedMenu}
                />
              ))}

            {layer !== 0 && (
              <Box
                py={0}
                px={2}
                display="flex"
                alignItems="center"
                justifyItems="center"
              >
                <Typography
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    color: theme.palette['Ambient/Grayscale/Gray-200'],
                  }}
                  variant="Paragraph/Medium"
                >
                  {openedMenu}
                </Typography>
                <Button
                  style={{
                    color: theme.palette['Ambient/Grayscale/Gray-200'],
                  }}
                  onClick={() => {
                    setLayer(0);
                  }}
                >
                  X
                </Button>
              </Box>
            )}

            {layer === 1 &&
              activeFirstLayerItem &&
              activeFirstLayerItem.submenus?.map(
                (subItem: FirstLayerSubmenu, i) => (
                  <LayerSubmenuItem
                    key={i}
                    menuOption={subItem}
                    handleClick={() => {
                      handlePressInLayerSubmenu(subItem);
                    }}
                    color={subItem.link && getMenuColor(subItem.link)}
                  />
                ),
              )}

            {layer === 2 &&
              activeSecondLayerItem &&
              activeSecondLayerItem.submenus?.map(
                (subItem: SecondLayerSubMenu, i) => (
                  <LayerSubmenuItem
                    key={i}
                    menuOption={subItem}
                    handleClick={() => {
                      handlePressInLayerSubmenu(subItem);
                    }}
                    color={getMenuColor(subItem.link)}
                  />
                ),
              )}
          </Stack>
          <Divider />
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            my={5}
          >
            <Typography variant="Paragraph/Medium" mr={1}>
              Desenvolvido por
            </Typography>

            <LogoImage src={logoGoing2} alt="Going2 Logo" />
          </Stack>
        </StackScroll>
      </Drawer>

      <Box width="100%">{children}</Box>
    </Box>
  );
}
